import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Printer, Mail } from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../lib/supabase';

// Print / Email actions for a native record whose customer- or vendor-facing
// PDF exists in packages/documents (NetSuite migration, task #66). Renders as
// header buttons next to Make Copy / Void on the native record pages.
//
//   <DocumentActions
//     recordId={id}
//     printDocs={[
//       { type: 'order-confirmation', label: 'Print / Download PDF' },
//       { type: 'packing-slip', label: 'Packing slip' },
//     ]}
//     emailDoc={{ type: 'order-confirmation', label: 'Email…' }}
//   />
//
// PRINT — opens GET /api/documents/<type>/<id>.pdf in a new tab. That route
// runs its OWN auth (Run's global verifyAuth skips *.pdf so browsers can open
// print tabs) and accepts the Supabase JWT as ?token=, so we append the current
// session token to the URL (a new tab can't send an Authorization header).
//
// EMAIL — POSTs /api/documents/<type>/<id>/queue. This is a DRAFT-FIRST flow:
// it does NOT send. The document is written to the outbox as pending_approval
// for a human to review and approve at /records/outbox. The button copy and the
// confirmation both say so explicitly. There is deliberately NO auto-send here.

export async function openPdf(type, recordId) {
  let token = '';
  try {
    const { data: { session } } = await supabase.auth.getSession();
    token = session?.access_token || '';
  } catch {
    // No session — the .pdf route will answer 401 and the new tab shows it.
  }
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  const url = `/api/documents/${encodeURIComponent(type)}/${encodeURIComponent(recordId)}.pdf${qs}`;
  window.open(url, '_blank', 'noopener');
}

export default function DocumentActions({ recordId, printDocs = [], emailDoc = null }) {
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(null); // { state, to } once queued

  const queueEmail = async () => {
    if (!emailDoc) return;
    const ok = window.confirm(
      'Queue this document for email?\n\n'
      + 'It will NOT be sent now. It is saved to the Documents Outbox as a draft '
      + '(pending approval) for someone to review and approve before it goes out.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `/api/documents/${encodeURIComponent(emailDoc.type)}/${encodeURIComponent(recordId)}/queue`
      );
      setQueued({
        state: data?.state || 'pending_approval',
        to: data?.to || data?.recipient || data?.recipient_email || null,
      });
    } catch (err) {
      window.alert(err.response?.data?.error || err.message || 'Failed to queue the document.');
    } finally {
      setBusy(false);
    }
  };

  if (!recordId) return null;

  return (
    <>
      {printDocs.map((d) => (
        <button
          key={d.type}
          className="nit-btn"
          onClick={() => openPdf(d.type, recordId)}
          title={`Open the ${d.label} PDF in a new tab`}
        >
          <Printer size={14} /> {d.label}
        </button>
      ))}

      {emailDoc && (
        <button
          className="nit-btn"
          disabled={busy}
          onClick={queueEmail}
          title="Queues a draft for review — does NOT send immediately"
        >
          <Mail size={14} /> {busy ? 'Queuing…' : emailDoc.label}
        </button>
      )}

      {queued && (
        <span className="nit-tag" title="The document is a draft awaiting approval; it has not been sent">
          Queued for approval{queued.to ? ` → ${queued.to}` : ''} —{' '}
          <Link to="/records/outbox">review in Outbox</Link>
        </span>
      )}
    </>
  );
}
